import { FC, useEffect, useState } from "react";
import { Col, Form, Row, Select, Spin } from "antd";
import type { FormInstance } from "antd";
import {
  useGetMasterDistrict,
  useGetMasterProvince,
  useGetMasterSubDistrict,
} from "hooks/useMasterData";
import type {
  MasterDistrictData,
  MasterProvinceData,
  MasterSubDistrictData,
} from "services/interface";

const { Option } = Select;

type ParcelDestinationAddressFieldsProps = {
  form: FormInstance;
  /** prefix ของฟิลด์ใน Form — default destination */
  namePrefix?: (string | number)[];
  fieldsDisabled?: boolean;
  /** Modal สร้างที่อยู่ — จังหวัด/อำเภอ/ตำบล คนละแถว */
  oneFieldPerRow?: boolean;
};

const ParcelDestinationAddressFields: FC<ParcelDestinationAddressFieldsProps> = ({
  form,
  namePrefix = ["destination"],
  fieldsDisabled = false,
  oneFieldPerRow = false,
}) => {
  const fieldColProps = oneFieldPerRow ? { xs: 24, span: 24 } : { xs: 24, md: 8 };
  const p = (field: string) => [...namePrefix, field];
  const prefixKey = namePrefix[0] as string;

  const patchDestination = (patch: Record<string, unknown>) => {
    const current = form.getFieldValue(prefixKey) || {};
    form.setFieldsValue({ [prefixKey]: { ...current, ...patch } });
  };

  const toMasterId = (value: unknown): number => {
    const n = Number(value);
    return Number.isFinite(n) && n > 0 ? n : 0;
  };

  const watchedProvinceId = Form.useWatch(p("province_id"), form);
  const watchedDistrictId = Form.useWatch(p("district_id"), form);

  const [provinceId, setProvinceId] = useState<number>(0);
  const [districtId, setDistrictId] = useState<number>(0);
  const [provinces, setProvinces] = useState<MasterProvinceData[]>([]);
  const [districts, setDistricts] = useState<MasterDistrictData[]>([]);
  const [subDistricts, setSubDistricts] = useState<MasterSubDistrictData[]>([]);

  const { data: masterProvinceData, isLoading: provinceLoading } = useGetMasterProvince();
  const { data: masterDistrictData, isLoading: districtLoading } =
    useGetMasterDistrict(provinceId);
  const { data: masterSubDistrictData, isLoading: subDistrictLoading } =
    useGetMasterSubDistrict(districtId);

  /** sync จากฟอร์มเมื่อเลือกที่อยู่จาก lookup (ไม่ผ่าน onChange ของ Select) */
  useEffect(() => {
    setProvinceId(toMasterId(watchedProvinceId));
  }, [watchedProvinceId]);

  useEffect(() => {
    setDistrictId(toMasterId(watchedDistrictId));
  }, [watchedDistrictId]);

  useEffect(() => {
    if (masterProvinceData) {
      setProvinces(masterProvinceData);
    }
  }, [masterProvinceData]);

  useEffect(() => {
    if (masterDistrictData) {
      setDistricts(masterDistrictData);
    } else {
      setDistricts([]);
    }
  }, [masterDistrictData]);

  useEffect(() => {
    if (masterSubDistrictData) {
      setSubDistricts(masterSubDistrictData);
    } else {
      setSubDistricts([]);
    }
  }, [masterSubDistrictData]);

  const applyProvinceText = (id: number) => {
    const item = provinces.find((x) => x.id === id);
    patchDestination({ state: item?.name_th || "" });
  };

  const applyDistrictText = (id: number) => {
    const item = districts.find((x) => x.id === id);
    patchDestination({ city: item?.name_th || "" });
  };

  const applySubDistrictText = (id: number) => {
    const item = subDistricts.find((x) => x.id === id);
    patchDestination({
      county: item?.name_th || "",
      postcode: item?.zip_code != null ? String(item.zip_code) : "",
    });
  };

  return (
    <Row gutter={16}>
      <Col {...fieldColProps}>
        <Form.Item
          name={p("province_id")}
          label="จังหวัด"
          rules={[{ required: true, message: "เลือกจังหวัด" }]}
        >
          <Select
            className="parcel-field-select"
            size="small"
            style={{ width: "100%" }}
            showSearch
            placeholder="จังหวัด"
            optionFilterProp="children"
            loading={provinceLoading}
            disabled={fieldsDisabled}
            notFoundContent={provinceLoading ? <Spin size="small" /> : null}
            onChange={(value: number) => {
              setProvinceId(value);
              setDistrictId(0);
              applyProvinceText(value);
              patchDestination({
                district_id: undefined,
                sub_district_id: undefined,
                city: undefined,
                county: undefined,
                postcode: undefined,
              });
            }}
          >
            {provinces.map((item) => (
              <Option key={item.id} value={item.id}>
                {item.name_th}
              </Option>
            ))}
          </Select>
        </Form.Item>
      </Col>
      <Col {...fieldColProps}>
        <Form.Item
          name={p("district_id")}
          label="อำเภอ"
          rules={[{ required: true, message: "เลือกอำเภอ" }]}
        >
          <Select
            className="parcel-field-select"
            size="small"
            style={{ width: "100%" }}
            showSearch
            placeholder="อำเภอ"
            optionFilterProp="children"
            disabled={fieldsDisabled || !provinceId}
            loading={districtLoading}
            notFoundContent={districtLoading ? <Spin size="small" /> : null}
            onChange={(value: number) => {
              setDistrictId(value);
              applyDistrictText(value);
              patchDestination({
                sub_district_id: undefined,
                county: undefined,
                postcode: undefined,
              });
            }}
          >
            {districts.map((item) => (
              <Option key={item.id} value={item.id}>
                {item.name_th}
              </Option>
            ))}
          </Select>
        </Form.Item>
      </Col>
      <Col {...fieldColProps}>
        <Form.Item
          name={p("sub_district_id")}
          label="ตำบล"
          rules={[{ required: true, message: "เลือกตำบล" }]}
        >
          <Select
            className="parcel-field-select"
            size="small"
            style={{ width: "100%" }}
            showSearch
            placeholder="ตำบล"
            optionFilterProp="children"
            disabled={fieldsDisabled || !districtId}
            loading={subDistrictLoading}
            notFoundContent={subDistrictLoading ? <Spin size="small" /> : null}
            onChange={(value: number) => {
              applySubDistrictText(value);
            }}
          >
            {subDistricts.map((item) => (
              <Option key={item.id} value={item.id}>
                {item.name_th}
              </Option>
            ))}
          </Select>
        </Form.Item>
      </Col>
      <Form.Item name={p("county")} hidden>
        <span />
      </Form.Item>
      <Form.Item name={p("city")} hidden>
        <span />
      </Form.Item>
      <Form.Item name={p("state")} hidden>
        <span />
      </Form.Item>
    </Row>
  );
};

export default ParcelDestinationAddressFields;
